import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Copy, Check, ExternalLink, Eye } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// OBS Studio Logo SVG
const OBSIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0zm0 2.16c5.44 0 9.84 4.4 9.84 9.84 0 5.44-4.4 9.84-9.84 9.84-5.44 0-9.84-4.4-9.84-9.84 0-5.44 4.4-9.84 9.84-9.84zM8.56 6.06c-2.19 0-3.97 1.78-3.97 3.97s1.78 3.97 3.97 3.97c.56 0 1.09-.12 1.57-.33-.19-.45-.3-.94-.3-1.46 0-2.06 1.67-3.73 3.73-3.73.52 0 1.01.11 1.46.3.21-.48.33-1.01.33-1.57 0-2.19-1.78-3.97-3.97-3.97-.56 0-1.09.12-1.57.33.19.45.3.94.3 1.46 0 .21-.02.42-.05.62-.2-.03-.4-.05-.62-.05-.52 0-1.01.11-1.46.3-.21-.48-.33-1.01-.33-1.57 0-.22.02-.43.05-.63-.38.07-.74.18-1.09.33zm6.88 4.15c-2.06 0-3.73 1.67-3.73 3.73s1.67 3.73 3.73 3.73 3.73-1.67 3.73-3.73-1.67-3.73-3.73-3.73z"/>
  </svg>
);

export const OBSCardsTab = ({ token }) => {
  const [challenges, setChallenges] = useState([]);
  const [selectedChallenge, setSelectedChallenge] = useState('');
  const [selectedTheme, setSelectedTheme] = useState('dark');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChallenges();
  }, []);

  const fetchChallenges = async () => {
    try {
      const response = await axios.get(`${API}/api/challenges`);
      const safeData = Array.isArray(response.data) ? response.data : [];
      setChallenges(safeData);
      if (safeData.length > 0) {
        // Default to first active challenge, or first challenge
        const activeChallenge = safeData.find(c => c.status === 'active');
        setSelectedChallenge(activeChallenge?.id || safeData[0].id);
      }
    } catch (error) {
      toast.error('Failed to load challenges');
    } finally {
      setLoading(false);
    }
  };

  const themes = [
    { value: 'dark', label: 'Dark (Green)', color: '#00ff00' },
    { value: 'neon', label: 'Neon (Cyan)', color: '#00ffff' },
    { value: 'red', label: 'Red', color: '#ff4444' },
    { value: 'gold', label: 'Gold', color: '#ffd700' },
  ];

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const obsUrl = selectedChallenge 
    ? `${baseUrl}/obs/fullscreen/${selectedChallenge}?theme=${selectedTheme}`
    : '';

  const copyToClipboard = () => {
    if (obsUrl) {
      navigator.clipboard.writeText(obsUrl);
      setCopied(true);
      toast.success('OBS Browser Source URL copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const openPreview = () => {
    if (obsUrl) {
      window.open(obsUrl, '_blank', 'width=1920,height=1080');
    }
  };

  const safeChallenges = Array.isArray(challenges) ? challenges : [];
  const selectedChallengeData = safeChallenges.find(c => c.id === selectedChallenge);

  if (loading) {
    return (
      <Card className="glass-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin w-8 h-8 border-4 border-green-500/30 border-t-green-500 rounded-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="glass-card border-purple-500/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-purple-400">
            <OBSIcon className="w-5 h-5" />
            OBS Browser Source (1920x1080)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-400 text-sm mb-4">
            Create fullscreen OBS browser source cards for your challenges. Perfect for stream starting soon screens, BRB screens, or dedicated challenge displays.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Challenge Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Select Challenge
              </label>
              <Select value={selectedChallenge} onValueChange={setSelectedChallenge}>
                <SelectTrigger className="bg-black/50 border-gray-700">
                  <SelectValue placeholder="Choose a challenge" />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700">
                  {safeChallenges.map((challenge) => (
                    <SelectItem key={challenge.id} value={challenge.id}>
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${
                          challenge.status === 'active' ? 'bg-green-500' : 
                          challenge.status === 'scheduled' ? 'bg-yellow-500' : 'bg-gray-500'
                        }`}></span>
                        <span>{challenge.title}</span>
                        <span className="text-gray-500 text-xs">({challenge.status})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Theme Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Select Theme
              </label>
              <Select value={selectedTheme} onValueChange={setSelectedTheme}>
                <SelectTrigger className="bg-black/50 border-gray-700">
                  <SelectValue placeholder="Choose a theme" />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700">
                  {themes.map((theme) => (
                    <SelectItem key={theme.value} value={theme.value}>
                      <div className="flex items-center gap-2">
                        <span 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: theme.color }}
                        ></span>
                        <span>{theme.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Generated URL */}
          {selectedChallenge && (
            <div className="bg-black/50 border border-gray-700 rounded-lg p-4 mb-4">
              <label className="block text-sm font-medium text-gray-400 mb-2">
                OBS Browser Source URL
              </label>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-black/50 px-3 py-2 rounded text-green-400 text-sm overflow-x-auto">
                  {obsUrl}
                </code>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={copyToClipboard}
                  className="flex-shrink-0"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button 
              onClick={copyToClipboard} 
              disabled={!selectedChallenge}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy URL
            </Button>
            <Button 
              variant="outline" 
              onClick={openPreview}
              disabled={!selectedChallenge}
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
            <Button 
              variant="outline" 
              onClick={() => window.open(obsUrl, '_blank')}
              disabled={!selectedChallenge}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open Fullscreen
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview Card */}
      {selectedChallengeData && (
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-lg">Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <div 
              className="relative rounded-lg overflow-hidden bg-black/50 border border-gray-700"
              style={{ 
                aspectRatio: '16/9',
                maxHeight: '300px'
              }}
            >
              {/* Static preview mockup instead of iframe to prevent layout issues */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center p-6">
                  <div 
                    className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                    style={{ 
                      background: `rgba(${themes.find(t => t.value === selectedTheme)?.color === '#00ff00' ? '0, 255, 0' : 
                                         themes.find(t => t.value === selectedTheme)?.color === '#00ffff' ? '0, 255, 255' :
                                         themes.find(t => t.value === selectedTheme)?.color === '#ff4444' ? '255, 68, 68' :
                                         '255, 215, 0'}, 0.2)`,
                      border: `2px solid ${themes.find(t => t.value === selectedTheme)?.color || '#00ff00'}`
                    }}
                  >
                  <OBSIcon className="w-8 h-8" style={{ color: themes.find(t => t.value === selectedTheme)?.color || '#00ff00' }} />
                  </div>
                  <h3 className="text-white font-bold text-lg mb-2">{selectedChallengeData.title}</h3>
                  <p className="text-gray-400 text-sm mb-2">{selectedChallengeData.game_name || 'No game selected'}</p>
                  <p className="text-2xl font-black" style={{ color: themes.find(t => t.value === selectedTheme)?.color || '#00ff00' }}>
                    ${selectedChallengeData.prize_amount || 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-4">
                    Click "Preview" or "Open Fullscreen" to see the actual 1920x1080 card
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* OBS Setup Instructions */}
      <Card className="glass-card border-blue-500/20">
        <CardHeader>
          <CardTitle className="text-lg text-blue-400">OBS Setup Instructions</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-gray-400 space-y-3">
          <div className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs font-bold">1</span>
            <p>In OBS, click <strong className="text-white">+</strong> under Sources and select <strong className="text-white">Browser</strong></p>
          </div>
          <div className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs font-bold">2</span>
            <p>Name it (e.g., "Challenge Card") and click <strong className="text-white">OK</strong></p>
          </div>
          <div className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs font-bold">3</span>
            <p>Paste the URL from above into the <strong className="text-white">URL</strong> field</p>
          </div>
          <div className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs font-bold">4</span>
            <p>Set <strong className="text-white">Width: 1920</strong> and <strong className="text-white">Height: 1080</strong></p>
          </div>
          <div className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs font-bold">5</span>
            <p>Click <strong className="text-white">OK</strong> and position as needed</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OBSCardsTab;
